import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

type PlaybackBoundaryReadinessContextValue = {
  readyItemIds: ReadonlySet<string>;
  setItemReady: (itemId: string, ready: boolean) => void;
};

const PlaybackBoundaryReadinessContext =
  createContext<PlaybackBoundaryReadinessContextValue>({
    readyItemIds: new Set(),
    setItemReady: () => undefined,
  });

export const PlaybackBoundaryReadinessProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [readyItemIds, setReadyItemIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const setItemReady = useCallback((itemId: string, ready: boolean) => {
    setReadyItemIds((current) => {
      if (current.has(itemId) === ready) return current;
      const next = new Set(current);
      if (ready) next.add(itemId);
      else next.delete(itemId);
      return next;
    });
  }, []);
  const value = useMemo(
    () => ({ readyItemIds, setItemReady }),
    [readyItemIds, setItemReady],
  );

  return (
    <PlaybackBoundaryReadinessContext.Provider value={value}>
      {children}
    </PlaybackBoundaryReadinessContext.Provider>
  );
};

export const usePlaybackBoundaryItemReady = (
  itemId: string | undefined,
): boolean => {
  const { readyItemIds } = useContext(PlaybackBoundaryReadinessContext);
  return itemId ? readyItemIds.has(itemId) : true;
};

export const usePlaybackBoundaryReadinessReporter = () =>
  useContext(PlaybackBoundaryReadinessContext).setItemReady;
